import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdUploadFile, MdCloudUpload, MdPreview, MdCheckCircle,
  MdError, MdWarning, MdClose, MdEmail,
} from 'react-icons/md';
import { gccFlowsService } from '../../services/gccFlowsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const STATUS_BADGE = {
  completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  pending: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
};

const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const IngestionPage = () => {
  const [singleFile, setSingleFile] = useState(null);
  const [reportDate, setReportDate] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const singleRef = useRef(null);

  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null); // {current, total}
  const [dragOver, setDragOver] = useState(false);
  const bulkRef = useRef(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [emailChecking, setEmailChecking] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  const fetchEmailLog = useCallback(async () => {
    try {
      setEmailLogLoading(true);
      const res = await gccFlowsService.getEmailLog({ limit: 100 });
      setEmailLog(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch {
      // silent
    } finally {
      setEmailLogLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await gccFlowsService.getUploadHistory();
      setHistory(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); fetchEmailLog(); }, [fetchHistory, fetchEmailLog]);

  const handleEmailCheck = async () => {
    setEmailChecking(true);
    setEmailMsg(null);
    setEmailResult(null);
    try {
      const res = await gccFlowsService.ingestEmailsNow();
      const d = res.data || {};
      setEmailResult(d);
      const matched = d.messages_matched || 0;
      const processed = d.attachments_processed || 0;
      const errors = (d.errors || []).length;
      let text;
      if (matched === 0) text = `Checked inbox — no new EFG emails (saw ${d.messages_seen || 0} total unread with attachments).`;
      else text = `Processed ${processed} attachment(s) from ${matched} email(s)${errors ? `, ${errors} error(s)` : ''}.`;
      setEmailMsg({ type: errors ? 'warning' : 'success', text });
      fetchHistory();
      fetchEmailLog();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Email check failed';
      setEmailMsg({ type: 'error', text: msg });
    } finally {
      setEmailChecking(false);
    }
  };

  const handlePreview = async () => {
    if (!singleFile) return;
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await gccFlowsService.previewExtract(singleFile);
      setPreviewData(res.data);
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.error || 'Preview failed' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async () => {
    if (!singleFile) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      const res = await gccFlowsService.uploadReport(singleFile, reportDate || undefined);
      const d = res.data;
      const warnings = d.warnings || [];
      const errors = d.errors || [];
      const msgType = errors.length ? 'error' : warnings.length ? 'warning' : 'success';
      const summary = `${d.tickers_parsed || 0} tickers, ${d.sectors_parsed || 0} sector rows, ${d.events_parsed || 0} events`;
      const text = errors.length
        ? `Upload had ${errors.length} error(s): ${errors.slice(0, 3).join('; ')}`
        : warnings.length
        ? `Uploaded ${d.records_created} records (${summary}) with ${warnings.length} warning(s)`
        : `Uploaded ${d.records_created} records for ${d.report_date} (${summary})`;
      setUploadMsg({ type: msgType, text });
      setSingleFile(null);
      setPreviewData(null);
      setReportDate('');
      if (singleRef.current) singleRef.current.value = '';
      fetchHistory();
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.error || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBulkMsg(null);
    setBulkResults([]);

    // Upload one file at a time to avoid nginx 60s gateway timeout on large
    // batches. Progress streams into `bulkResults` as each file completes.
    const accumulated = [];
    const total = bulkFiles.length;
    for (let i = 0; i < total; i++) {
      const file = bulkFiles[i];
      setBulkProgress({ current: i + 1, total });
      try {
        const res = await gccFlowsService.uploadReport(file);
        const d = res.data;
        accumulated.push({
          file: file.name,
          status: d.status || 'completed',
          report_date: d.report_date,
          records: d.records_created,
          warnings: (d.warnings || []).length,
          errors: (d.errors || []).length,
        });
      } catch (err) {
        accumulated.push({
          file: file.name,
          status: 'failed',
          error: err.response?.data?.error || err.message || 'Upload failed',
        });
      }
      setBulkResults([...accumulated]);
    }

    const completed = accumulated.filter((r) => r.status === 'completed').length;
    const warning = accumulated.filter((r) => r.status === 'warning').length;
    const failed = accumulated.filter((r) => r.status === 'failed').length;
    const parts = [];
    if (completed) parts.push(`${completed} uploaded`);
    if (warning) parts.push(`${warning} with warnings`);
    if (failed) parts.push(`${failed} failed`);
    setBulkMsg({
      type: failed ? 'warning' : 'success',
      text: parts.join(', ') || 'Done',
    });
    setBulkFiles([]);
    setBulkUploading(false);
    setBulkProgress(null);
    fetchHistory();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.name.toLowerCase().endsWith('.xlsx'));
    setBulkFiles((prev) => [...prev, ...files]);
  };

  const removeBulkFile = (idx) => setBulkFiles((prev) => prev.filter((_, i) => i !== idx));

  const inputCls =
    'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#c9a96e]/30 focus:border-[#c9a96e] outline-none';
  const btnPrimary =
    'px-4 py-2 rounded-lg text-sm font-medium bg-[#c9a96e] text-white hover:bg-[#b8954f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSecondary =
    'px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors';

  return (
    <div className="space-y-6">
      {/* Email Inbox Auto-Pull */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <MdEmail className="text-[#c9a96e]" />
              Email Inbox Auto-Pull
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Pulls EFG xlsx attachments from <span className="font-mono">admin@maghaweer.ai</span>.
              Runs automatically daily at 12:00 UAE — click to check now.
            </p>
          </div>
          <button onClick={handleEmailCheck} disabled={emailChecking} className={btnPrimary}>
            {emailChecking ? 'Checking...' : 'Check Email Now'}
          </button>
        </div>

        {emailMsg && (
          <div
            className={`mt-3 flex items-start gap-2 rounded-lg px-4 py-2.5 text-sm ${
              emailMsg.type === 'success'
                ? 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : emailMsg.type === 'warning'
                ? 'border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                : 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}
          >
            {emailMsg.type === 'success' ? <MdCheckCircle className="mt-0.5 shrink-0" /> : emailMsg.type === 'warning' ? <MdWarning className="mt-0.5 shrink-0" /> : <MdError className="mt-0.5 shrink-0" />}
            <span className="flex-1">{emailMsg.text}</span>
            <button onClick={() => setEmailMsg(null)} className="ml-auto shrink-0"><MdClose /></button>
          </div>
        )}

        {emailResult?.reports?.length > 0 && (
          <div className="mt-3 space-y-1">
            {emailResult.reports.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 px-3 py-1">
                <MdCheckCircle className="shrink-0" />
                <span className="truncate">{r.filename}</span>
                <span className="ml-auto shrink-0">{r.report_date} · {r.records} records</span>
              </div>
            ))}
          </div>
        )}
        {emailResult?.errors?.length > 0 && (
          <div className="mt-3 space-y-1">
            {emailResult.errors.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 px-3 py-1">
                <MdError className="shrink-0" />
                <span className="truncate">{e.filename || 'message'}</span>
                <span className="ml-auto shrink-0 text-right max-w-md truncate">{e.error}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email Activity Log */}
      <details className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 cursor-pointer flex items-center justify-between gap-2 select-none">
          <span className="font-serif text-base text-gray-900 dark:text-gray-100">Email Activity Log</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {emailLog.length} entr{emailLog.length === 1 ? 'y' : 'ies'} · per-email status, errors, attachment counts
          </span>
        </summary>
        <div className="border-t border-gray-200 dark:border-gray-800">
          {emailLogLoading ? (
            <LoadingSpinner message="Loading..." />
          ) : emailLog.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
              No activity yet. Will populate as the daily auto-pull runs (or on a manual "Check Email Now").
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-[#0d1117]">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Processed</th>
                    <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Subject</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Seen / Matched / OK</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {emailLog.map((row) => {
                    const statusColor = {
                      success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
                      partial_success: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
                      no_match: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
                      failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
                    }[row.status] || 'bg-gray-100 dark:bg-gray-800';
                    return (
                      <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {row.processed_at ? new Date(row.processed_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase ${statusColor}`}>
                            {row.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 max-w-md truncate">
                          {row.subject || '(no subject)'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                          {row.attachments_seen} / {row.attachments_matched} / {row.attachments_succeeded}
                        </td>
                        <td className="px-3 py-1.5 text-red-600 dark:text-red-400 max-w-md truncate" title={row.error_message || ''}>
                          {row.error_message || ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>

      {/* Single Upload */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <MdUploadFile className="text-[#c9a96e]" />
          Single Upload
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">
              EFG Daily Flows .xlsx
            </label>
            <input
              ref={singleRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setSingleFile(e.target.files[0] || null);
                setPreviewData(null);
                setUploadMsg(null);
              }}
              className="block w-full text-sm text-gray-800 dark:text-gray-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 dark:file:border-gray-700 file:text-sm file:bg-white dark:file:bg-[#0d1117] file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-800 cursor-pointer"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">
              Report Date (optional)
            </label>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className={`${inputCls} w-full`}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handlePreview} disabled={!singleFile || previewing} className={btnSecondary}>
              {previewing ? 'Parsing...' : 'Preview'}
            </button>
            <button onClick={handleUpload} disabled={!singleFile || uploading} className={btnPrimary}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>

        {uploadMsg && (
          <div
            className={`mt-3 flex items-start gap-2 rounded-lg px-4 py-2.5 text-sm ${
              uploadMsg.type === 'success'
                ? 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : uploadMsg.type === 'warning'
                ? 'border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                : 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}
          >
            {uploadMsg.type === 'success' ? <MdCheckCircle className="mt-0.5 shrink-0" /> : uploadMsg.type === 'warning' ? <MdWarning className="mt-0.5 shrink-0" /> : <MdError className="mt-0.5 shrink-0" />}
            <span className="flex-1">{uploadMsg.text}</span>
            <button onClick={() => setUploadMsg(null)} className="ml-auto shrink-0"><MdClose /></button>
          </div>
        )}

        {previewing && <LoadingSpinner message="Parsing workbook..." />}
        {previewData && !previewing && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <MdPreview className="text-[#c9a96e]" />
                Preview: {previewData.workbook_date || 'Date not detected'}
              </span>
              <button onClick={() => setPreviewData(null)} className="text-gray-400 hover:text-gray-600">
                <MdClose />
              </button>
            </div>

            {/* Counts grid */}
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              {[
                { label: 'Tickers', value: previewData.counts?.tickers },
                { label: 'Sector rows', value: previewData.counts?.sectors },
                { label: 'Index events', value: previewData.counts?.events },
              ].map((c) => (
                <div key={c.label} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{c.label}</div>
                  <div className="text-xl font-serif text-gray-900 dark:text-gray-100 tabular-nums">{c.value ?? 0}</div>
                </div>
              ))}
            </div>

            {/* Per-market dates */}
            {previewData.market_dates && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Per-market report dates:&nbsp;
                {Object.entries(previewData.market_dates).map(([m, d]) => (
                  <span key={m} className="mr-3">
                    <span className="font-medium">{m}</span>: {d || 'n/a'}
                  </span>
                ))}
              </div>
            )}

            {previewData.warnings?.length > 0 && (
              <div className="space-y-1">
                {previewData.warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                    <MdWarning className="shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
            {previewData.errors?.length > 0 && (
              <div className="space-y-1">
                {previewData.errors.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                    <MdError className="shrink-0" /> {e}
                  </div>
                ))}
              </div>
            )}

            {/* Sample tickers */}
            {previewData.sample_tickers?.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-[#0d1117] px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Sample tickers
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-[#0d1117]">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Market</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Ticker</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net 1d ($mn)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net WTD</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net YTD</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">FO %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {previewData.sample_tickers.map((row, i) => {
                        const net = Number(row.net_usd_1d || 0);
                        const cls = net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                        return (
                          <tr key={i}>
                            <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.market}</td>
                            <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 font-medium">{row.ticker}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${cls}`}>{fmt(row.net_usd_1d)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${Number(row.net_usd_wtd) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(row.net_usd_wtd)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${Number(row.net_usd_ytd) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(row.net_usd_ytd)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{row.total_fo_pct ? `${(Number(row.total_fo_pct) * 100).toFixed(2)}%` : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Upload */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <MdCloudUpload className="text-[#c9a96e]" />
          Bulk Historical Upload
        </h3>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => bulkRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[#c9a96e] bg-[#c9a96e]/5' : 'border-gray-300 dark:border-gray-700 hover:border-[#c9a96e]/50'
          }`}
        >
          <MdCloudUpload className="text-3xl text-gray-400 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Drop EFG .xlsx files here or <span className="text-[#c9a96e] font-medium">browse</span>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">.xlsx — re-uploads replace existing rows for the same date</p>
          <input
            ref={bulkRef}
            type="file"
            multiple
            accept=".xlsx"
            className="hidden"
            onChange={(e) => setBulkFiles((prev) => [...prev, ...Array.from(e.target.files)])}
          />
        </div>

        {bulkFiles.length > 0 && (
          <div className="mt-3 space-y-1">
            {bulkFiles.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-[#0d1117] rounded-lg px-3 py-2 text-sm">
                <span className="text-gray-800 dark:text-gray-200 truncate">{f.name}</span>
                <button onClick={() => removeBulkFile(i)} className="text-gray-400 hover:text-red-500 ml-2"><MdClose /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <button onClick={handleBulkUpload} disabled={bulkUploading} className={btnPrimary}>
                {bulkUploading
                  ? bulkProgress
                    ? `Uploading ${bulkProgress.current}/${bulkProgress.total}...`
                    : 'Uploading...'
                  : `Upload ${bulkFiles.length} files`}
              </button>
              <button onClick={() => setBulkFiles([])} className={btnSecondary}>Clear</button>
            </div>
          </div>
        )}

        {bulkMsg && (
          <div
            className={`mt-3 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm ${
              bulkMsg.type === 'success'
                ? 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : bulkMsg.type === 'warning'
                ? 'border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                : 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}
          >
            {bulkMsg.type === 'success' ? <MdCheckCircle /> : bulkMsg.type === 'warning' ? <MdWarning /> : <MdError />}
            {bulkMsg.text}
          </div>
        )}

        {bulkResults && bulkResults.length > 0 && (
          <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
            {bulkResults.map((r, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded ${
                  r.status === 'completed' ? 'text-green-600 dark:text-green-400'
                    : r.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {r.status === 'completed' ? <MdCheckCircle className="shrink-0" />
                  : r.status === 'warning' ? <MdWarning className="shrink-0" />
                  : <MdError className="shrink-0" />}
                <span className="truncate">{r.file}</span>
                <span className="ml-auto shrink-0">
                  {r.report_date && `${r.report_date} · `}
                  {r.records != null ? `${r.records} records` : (r.error || r.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-serif text-base text-gray-900 dark:text-gray-100">Upload History</h3>
        </div>
        {historyLoading ? (
          <LoadingSpinner message="Loading history..." />
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <MdUploadFile className="text-3xl mx-auto mb-2 opacity-40" />
            <p className="text-sm">No uploads yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Report Date</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Filename</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Records</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Uploaded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {history.map((h, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap">{h.report_date}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[280px]">{h.filename || '-'}</td>
                    <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300 tabular-nums">{h.records_created || 0}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${STATUS_BADGE[h.status] || STATUS_BADGE.completed}`}>
                        {h.status || 'completed'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                      {h.created_at ? new Date(h.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngestionPage;
